/**
 * Klaviyo Shared API Helpers (Story 12.7, Task 2)
 *
 * Provides common helpers for Klaviyo API calls:
 * - Header construction with Bearer token + revision header
 * - Metric name → ID resolution (cached in KV for 24h)
 * - Metric aggregates fetching
 * - Account info fetching
 *
 * All API calls use: https://a.klaviyo.com/api/
 * Auth header: "Bearer {access_token}"
 * Revision header: "revision: 2026-01-15" (per ADR-014)
 */

import { API_VERSIONS } from "../config";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import type { KVStore } from "../cache";
import { parseKlaviyoApiError, KlaviyoQuotaExhaustedError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

export const KLAVIYO_API_BASE = "https://a.klaviyo.com/api";
const API_TIMEOUT_MS = 25_000;
/** Safety guard: max pages for cursor-paginated loops (100 items/page × 20 = 2,000 items) */
export const MAX_PAGES = 20;

/** ADR-017: Max retries for retryable errors (429, 5xx) */
const MAX_RETRIES = 3;
/** ADR-017: Exponential backoff base delays in ms (1s → 2s → 4s) */
const RETRY_DELAYS_MS = [1000, 2000, 4000];
const METRIC_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// ============================================================================
// Types
// ============================================================================

interface KlaviyoMetricEntry {
  type: "metric";
  id: string;
  attributes: {
    name: string;
    created: string;
    updated: string;
    integration: { object: string; id: string; name: string } | null;
  };
}

interface KlaviyoMetricsListResponse {
  data: KlaviyoMetricEntry[];
  links?: { next?: string };
}

interface KlaviyoMetricAggregatesResponse {
  data: {
    type: string;
    attributes: {
      dates: string[];
      data: Array<{
        dimensions: string[];
        measurements: Record<string, (number | null)[]>;
      }>;
    };
  };
}

interface KlaviyoAccountEntry {
  type: "account";
  id: string;
  attributes: {
    test_account: boolean;
    contact_information: {
      default_sender_name: string;
      default_sender_email: string;
      website_url: string;
      organization_name: string;
    };
    industry: string;
    timezone: string;
    preferred_currency: string;
    public_api_key: string;
    locale: string;
  };
}

interface KlaviyoAccountsResponse {
  data: KlaviyoAccountEntry[];
}

export interface KlaviyoAccountInfo {
  accountId: string;
  accountName: string;
  timezone: string;
  currency: string;
  isTestAccount: boolean;
}

export interface KlaviyoMetricAggregateResult {
  dates: string[];
  values: (number | null)[];
}

// ============================================================================
// Header Builder
// ============================================================================

/**
 * Builds Klaviyo API request headers.
 * All Klaviyo data API calls require Bearer token + revision header.
 */
export function buildKlaviyoHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    revision: API_VERSIONS.klaviyo,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// ============================================================================
// Metric Name → ID Resolution (cached in KV)
// ============================================================================

/**
 * Fetches all metrics from Klaviyo and builds a name → ID mapping.
 * Cached in KV for 24 hours to respect the 10 req/s rate limit on this endpoint.
 *
 * @param kv - KV store binding
 * @param accessToken - Bearer token
 * @param tenantId - For cache key scoping
 * @param integrationId - For cache key scoping
 * @returns Map of metric name → metric ID
 */
export async function fetchMetricsList(
  kv: KVStore,
  accessToken: string,
  tenantId: string,
  integrationId: string
): Promise<Map<string, string>> {
  const cacheKey = `klaviyo:metrics-map:${tenantId}:${integrationId}`;

  // Try cache first
  const cached = await kv.get(cacheKey);
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as Record<string, string>;
      return new Map(Object.entries(parsed));
    } catch {
      // Cache corrupted, fetch fresh
    }
  }

  // Fetch from API with cursor pagination
  const headers = buildKlaviyoHeaders(accessToken);
  const metricMap = new Map<string, string>();
  let nextUrl: string | null = `${KLAVIYO_API_BASE}/metrics/?page[size]=100`;
  let pageCount = 0;

  while (nextUrl && pageCount < MAX_PAGES) {
    pageCount++;
    const result = await safeFetch(nextUrl, { method: "GET", headers }, API_TIMEOUT_MS);

    if (!result.success) {
      throw new Error(
        sanitizeErrorMessage(`Klaviyo metrics list fetch failed: ${result.error.message}`)
      );
    }

    const response = result.data;
    if (!response.ok) {
      let errorBody: unknown = {};
      try { errorBody = await response.json(); } catch { /* ignore */ }
      const parsed = parseKlaviyoApiError(errorBody, response.status);
      throw new Error(
        sanitizeErrorMessage(parsed?.message ?? `Klaviyo metrics list failed: HTTP ${response.status}`)
      );
    }

    const data = (await response.json()) as KlaviyoMetricsListResponse;

    for (const metric of data.data) {
      metricMap.set(metric.attributes.name, metric.id);
    }

    nextUrl = data.links?.next || null;
  }

  // Cache the mapping for 24h
  const cacheValue = JSON.stringify(Object.fromEntries(metricMap));
  await kv.put(cacheKey, cacheValue, { expirationTtl: Math.floor(METRIC_CACHE_TTL_MS / 1000) });

  return metricMap;
}

// ============================================================================
// Metric Aggregates Fetch
// ============================================================================

/**
 * Fetches metric aggregates from Klaviyo's POST /api/metric-aggregates/ endpoint.
 *
 * @param accessToken - Bearer token
 * @param metricId - Resolved metric ID from fetchMetricsList()
 * @param dateRange - Start and end dates
 * @param measurements - Array of measurement types: ["count"] or ["count", "sum_value"]
 * @param timezone - Timezone for aggregation (from client/tenant config)
 * @returns Dates and corresponding values
 */
export async function fetchMetricAggregates(
  accessToken: string,
  metricId: string,
  dateRange: { start: Date; end: Date },
  measurements: string[],
  timezone: string = "UTC"
): Promise<Record<string, KlaviyoMetricAggregateResult>> {
  const headers = buildKlaviyoHeaders(accessToken);

  const body = {
    data: {
      type: "metric-aggregate",
      attributes: {
        metric_id: metricId,
        measurements,
        interval: "day",
        filter: [
          `greater-or-equal(datetime,${dateRange.start.toISOString()})`,
          `less-than(datetime,${dateRange.end.toISOString()})`,
        ],
        timezone,
      },
    },
  };

  // ADR-017: Retry with exponential backoff for 429/5xx responses.
  // safeFetch returns adapterErr (with retryable flag + retryAfterMs) for non-OK responses,
  // and adapterOk only when response.ok === true. Retry logic lives in the error branch.
  let lastError: Error | null = null;
  let lastRetryAfterMs: number | undefined;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      // Respect Retry-After header from provider when available (AC #7),
      // falling back to exponential backoff (ADR-017: 1s → 2s → 4s)
      const backoff = RETRY_DELAYS_MS[attempt - 1] ?? 4000;
      const delay = Math.max(lastRetryAfterMs ?? 0, backoff);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    const result = await safeFetch(
      `${KLAVIYO_API_BASE}/metric-aggregates/`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      },
      API_TIMEOUT_MS
    );

    if (!result.success) {
      if (result.error.retryable && attempt < MAX_RETRIES) {
        lastError = new Error(result.error.message);
        lastRetryAfterMs = result.error.retryAfterMs;
        continue;
      }
      throw new Error(
        sanitizeErrorMessage(`Klaviyo metric aggregates fetch failed: ${result.error.message}`)
      );
    }

    // safeFetch only returns success when response.ok — parse directly
    const data = (await result.data.json()) as KlaviyoMetricAggregatesResponse;
    const attrs = data.data.attributes;
    const dates = attrs.dates;

    const results: Record<string, KlaviyoMetricAggregateResult> = {};
    const firstDataEntry = attrs.data[0];
    if (firstDataEntry) {
      for (const measurement of measurements) {
        const values = firstDataEntry.measurements[measurement] || [];
        results[measurement] = { dates, values };
      }
    }

    return results;
  }

  // All retries exhausted
  throw lastError ?? new Error("Klaviyo metric aggregates failed after retries");
}

// ============================================================================
// Account Info Fetch
// ============================================================================

/**
 * Fetches Klaviyo account info via GET /api/accounts/.
 * Used after OAuth callback to get account name, and for connection validation.
 *
 * @param accessToken - Bearer token
 * @returns Account info or null on failure
 */
/** Result type for fetchKlaviyoAccounts — includes error detail when available */
export interface KlaviyoAccountResult {
  account: KlaviyoAccountInfo | null;
  /** Structured error code from safeFetch (e.g., "token_expired", "rate_limited") — null on success or parse failure */
  errorCode?: string;
}

export async function fetchKlaviyoAccounts(
  accessToken: string
): Promise<KlaviyoAccountInfo | null> {
  const result = await fetchKlaviyoAccountsWithDetail(accessToken);
  return result.account;
}

/**
 * Fetches Klaviyo account info with error detail for callers that need it (e.g., validateConnection).
 */
export async function fetchKlaviyoAccountsWithDetail(
  accessToken: string
): Promise<KlaviyoAccountResult> {
  const headers = buildKlaviyoHeaders(accessToken);

  const result = await safeFetch(
    `${KLAVIYO_API_BASE}/accounts/`,
    { method: "GET", headers },
    API_TIMEOUT_MS
  );

  if (!result.success) {
    return { account: null, errorCode: result.error.code };
  }

  try {
    const data = (await result.data.json()) as KlaviyoAccountsResponse;
    const account = data.data[0];
    if (!account) return { account: null };

    return {
      account: {
        accountId: account.id,
        accountName: account.attributes.contact_information.organization_name || "Klaviyo Account",
        timezone: account.attributes.timezone,
        currency: account.attributes.preferred_currency,
        isTestAccount: account.attributes.test_account,
      },
    };
  } catch {
    return { account: null, errorCode: "invalid_response" };
  }
}

// ============================================================================
// Story 12.8: Reporting API Quota Tracking (daily KV counter)
// ============================================================================
//
// The Klaviyo Reporting API caps at 225/day per OAuth app instance (1 req/s burst,
// 2/min steady). We track consumption with a cheap per-integration daily counter
// keyed by `klaviyo:quota:{integrationId}:{YYYY-MM-DD}`, TTL 25h so it auto-expires
// next day. Read-modify-write races are acceptable: occasional double-count
// undercounts remaining budget (safe direction).

/** Hard stop for Reporting API calls. Below 225 cap to leave room for retry bursts + race. */
export const KLAVIYO_REPORTING_QUOTA_HARD_LIMIT = 200;
/** Warn threshold: emit structured log when counter crosses this value. */
export const KLAVIYO_REPORTING_QUOTA_WARN_THRESHOLD = 150;
/** Actual Klaviyo Reporting API cap (for reference only; we never let counter reach this). */
export const KLAVIYO_REPORTING_QUOTA_ACTUAL_CAP = 225;
/** TTL for the counter. 25h ensures the counter survives midnight UTC rollover and then expires. */
const REPORTING_QUOTA_TTL_SECONDS = 25 * 60 * 60;

function getReportingQuotaKey(integrationId: string, date: Date = new Date()): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `klaviyo:quota:${integrationId}:${yyyy}-${mm}-${dd}`;
}

/**
 * Reads the current Reporting API quota count for this integration (today, UTC).
 * Returns 0 if the counter key is not yet set.
 */
export async function getReportingQuotaUsage(
  kv: KVStore,
  integrationId: string
): Promise<number> {
  const key = getReportingQuotaKey(integrationId);
  const raw = await kv.get(key);
  if (!raw) return 0;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

/**
 * Atomically (best-effort) increments the Reporting API quota counter.
 * Race conditions at our scale are acceptable — a double-count undercounts
 * remaining budget slightly, which is the safe direction.
 *
 * @returns new count after increment
 */
export async function incrementReportingQuota(
  kv: KVStore,
  integrationId: string
): Promise<number> {
  const key = getReportingQuotaKey(integrationId);
  const current = await getReportingQuotaUsage(kv, integrationId);
  const next = current + 1;
  await kv.put(key, String(next), { expirationTtl: REPORTING_QUOTA_TTL_SECONDS });
  return next;
}

/**
 * Checks whether the Reporting API budget is healthy.
 *
 * @returns "ok" when safely below the warn threshold,
 *          "warn" when approaching the cap (≥ 150),
 *          "exhausted" when at/above the hard limit (≥ 200).
 */
export async function checkReportingQuotaBudget(
  kv: KVStore,
  integrationId: string
): Promise<"ok" | "warn" | "exhausted"> {
  const count = await getReportingQuotaUsage(kv, integrationId);
  if (count >= KLAVIYO_REPORTING_QUOTA_HARD_LIMIT) return "exhausted";
  if (count >= KLAVIYO_REPORTING_QUOTA_WARN_THRESHOLD) return "warn";
  return "ok";
}

// ============================================================================
// Story 12.8: Mapped Metrics Resolution (cached 24h)
// ============================================================================

const MAPPED_METRIC_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Resolves the account's mapped conversion metric ID via GET /api/mapped-metrics.
 *
 * The Klaviyo Reporting API (campaign, flow, segment reports) requires a
 * `conversion_metric_id` on every request. This value is account-scoped —
 * typically "Placed Order" but may be a custom metric depending on the
 * account's revenue configuration.
 *
 * Result cached in KV for 24h since this rarely changes. Cache key includes
 * tenantId + integrationId so demo-mode and multi-tenant are correctly scoped.
 *
 * Why: avoids spending 1 Reporting API quota call on every fetch to resolve
 * something that almost never changes.
 *
 * @returns the resolved conversion_metric_id, or null if the API call fails
 *          or the account has no mapped metric (graceful degradation).
 */
export async function fetchMappedMetric(
  kv: KVStore,
  accessToken: string,
  tenantId: string,
  integrationId: string
): Promise<string | null> {
  const cacheKey = `klaviyo:mapped-metric:${tenantId}:${integrationId}`;

  // Try cache first
  const cached = await kv.get(cacheKey);
  if (cached) return cached;

  const headers = buildKlaviyoHeaders(accessToken);
  const result = await safeFetch(
    `${KLAVIYO_API_BASE}/mapped-metrics/`,
    { method: "GET", headers },
    API_TIMEOUT_MS
  );

  if (!result.success) {
    // Graceful degradation: mapped metric lookup failure does not block fetchMetrics
    return null;
  }

  // Note: no `if (!result.data.ok)` check here — `safeFetch` only returns
  // `success: true` when `response.ok === true` (see safe-fetch.ts). The
  // non-OK case is already handled by the `!result.success` branch above.

  try {
    const body = (await result.data.json()) as {
      data?: Array<{
        type: string;
        id: string;
        attributes?: { name?: string };
      }>;
    };
    const entries = body.data ?? [];
    // Prefer "Placed Order" if present; otherwise fall back to first entry
    const placedOrder = entries.find((e) => e.attributes?.name === "Placed Order");
    const chosen = placedOrder ?? entries[0];
    if (!chosen) return null;

    await kv.put(cacheKey, chosen.id, {
      expirationTtl: Math.floor(MAPPED_METRIC_CACHE_TTL_MS / 1000),
    });
    return chosen.id;
  } catch {
    return null;
  }
}

// ============================================================================
// Story 12.8: Reporting API Types
// ============================================================================

/** JSON:API response shape shared by /values-reports endpoints. */
interface KlaviyoValuesReportResponse<TGroupings extends Record<string, unknown>> {
  data?: {
    type: string;
    attributes?: {
      results?: Array<{
        groupings: TGroupings;
        statistics: Record<string, number | null>;
      }>;
    };
  };
  links?: { next?: string };
}

export interface KlaviyoCampaignGroupings {
  campaign_id?: string;
  campaign_message_id?: string;
  campaign_message_name?: string;
  send_channel?: string;
  /** Campaign send timestamp (ISO 8601). Returned by Klaviyo when available. */
  send_time?: string;
}

export interface KlaviyoFlowGroupings {
  flow_id?: string;
  flow_message_id?: string;
  flow_name?: string;
  flow_message_name?: string;
  send_channel?: string;
}

export interface KlaviyoFormGroupings {
  form_id?: string;
  form_version_id?: string;
}

export interface KlaviyoSegmentGroupings {
  segment_id?: string;
  segment_name?: string;
}

/** One entry produced by a Reporting API call, with groupings + raw statistics. */
export interface KlaviyoCampaignReportEntry {
  groupings: KlaviyoCampaignGroupings;
  statistics: Record<string, number | null>;
}

export interface KlaviyoFlowReportEntry {
  groupings: KlaviyoFlowGroupings;
  statistics: Record<string, number | null>;
}

export interface KlaviyoFormReportEntry {
  groupings: KlaviyoFormGroupings;
  statistics: Record<string, number | null>;
}

export interface KlaviyoSegmentReportEntry {
  groupings: KlaviyoSegmentGroupings;
  statistics: Record<string, number | null>;
}

// ============================================================================
// Story 12.8: Campaign/Flow/Form/Segment Report Statistics
// ============================================================================
//
// The full list of statistics available via `/api/campaign-values-reports` and
// `/api/flow-values-reports`. We fetch all of them in a single request to
// minimize API quota consumption — each full fetch consumes exactly 1 quota
// call regardless of how many statistics are requested.

export const KLAVIYO_CAMPAIGN_STATISTICS = [
  "recipients",
  "delivered",
  "delivery_rate",
  "opens",
  "opens_unique",
  "open_rate",
  "clicks",
  "clicks_unique",
  "click_rate",
  "click_to_open_rate",
  "bounced",
  "bounce_rate",
  "bounced_or_failed",
  "failed",
  "failed_rate",
  "unsubscribes",
  "unsubscribe_uniques",
  "unsubscribe_rate",
  "spam_complaints",
  "spam_complaint_rate",
  "conversions",
  "conversion_uniques",
  "conversion_rate",
  "conversion_value",
  "average_order_value",
  "revenue_per_recipient",
  "message_segment_count_sum",
  "text_message_credit_usage_amount",
  "text_message_spend",
  "text_message_roi",
] as const;

/** Flow reports share the exact same 30 statistics as campaign reports. */
export const KLAVIYO_FLOW_STATISTICS = KLAVIYO_CAMPAIGN_STATISTICS;

/** Form values reports expose 9 statistics — conversion_metric_id is NOT required. */
export const KLAVIYO_FORM_STATISTICS = [
  "viewed_form",
  "viewed_form_uniques",
  "submits",
  "submit_rate",
  "closed_form",
  "closed_form_uniques",
  "qualified_form",
  "viewed_form_step",
  "submitted_form_step",
] as const;

/** Segment values reports expose 4 growth statistics. */
export const KLAVIYO_SEGMENT_STATISTICS = [
  "total_members",
  "members_added",
  "members_removed",
  "net_members_changed",
] as const;

// ============================================================================
// Story 12.8: Reporting API Core Helper
// ============================================================================

/** Options controlling one `/values-reports` call. */
interface ReportingApiCallOptions {
  kv: KVStore;
  integrationId: string;
  accessToken: string;
  endpoint: "campaign" | "flow" | "form" | "segment";
  dateRange: { start: Date; end: Date };
  statistics: readonly string[];
  /** Required for campaign/flow/segment; MUST be omitted for form reports. */
  conversionMetricId?: string | null;
}

const REPORTING_ENDPOINT_MAP = {
  campaign: { path: "campaign-values-reports", type: "campaign-values-report" },
  flow: { path: "flow-values-reports", type: "flow-values-report" },
  form: { path: "form-values-reports", type: "form-values-report" },
  segment: { path: "segment-values-reports", type: "segment-values-report" },
} as const;

/**
 * Internal core helper used by all 4 public Reporting API helpers.
 *
 * Responsibilities:
 * 1. Short-circuit if quota counter is at/above hard limit (throws `KlaviyoQuotaExhaustedError`)
 * 2. POST to the correct `/values-reports/` endpoint with a single page of results
 * 3. Handle ADR-017 retry with `Retry-After` header respect (1s → 2s → 4s)
 * 4. MAX_PAGES=20 pagination guard (rare; typical response is single-page)
 * 5. Increment quota counter on each successful call
 */
async function callReportingApi<TEntry extends { groupings: unknown; statistics: Record<string, number | null> }>(
  opts: ReportingApiCallOptions
): Promise<TEntry[]> {
  // Gate 1: short-circuit if quota already exhausted (checked upfront by caller
  // but also here to defend against mid-fetch exhaustion during retry bursts).
  const budget = await checkReportingQuotaBudget(opts.kv, opts.integrationId);
  if (budget === "exhausted") {
    const count = await getReportingQuotaUsage(opts.kv, opts.integrationId);
    throw new KlaviyoQuotaExhaustedError(count, KLAVIYO_REPORTING_QUOTA_HARD_LIMIT);
  }

  const headers = buildKlaviyoHeaders(opts.accessToken);
  const endpointMeta = REPORTING_ENDPOINT_MAP[opts.endpoint];
  const url = `${KLAVIYO_API_BASE}/${endpointMeta.path}/`;

  // Request body — form reports MUST omit conversion_metric_id or the API returns 400.
  const attributes: Record<string, unknown> = {
    timeframe: {
      start: opts.dateRange.start.toISOString(),
      end: opts.dateRange.end.toISOString(),
    },
    statistics: opts.statistics,
  };
  if (opts.endpoint !== "form" && opts.conversionMetricId) {
    attributes.conversion_metric_id = opts.conversionMetricId;
  }

  const body = {
    data: {
      type: endpointMeta.type,
      attributes,
    },
  };

  const entries: TEntry[] = [];
  let pageCount = 0;
  let nextUrl: string | null = url;
  let lastError: Error | null = null;
  let lastRetryAfterMs: number | undefined;

  // Pagination loop (Klaviyo uses JSON:API cursor-based pagination; typical response single-page)
  while (nextUrl && pageCount < MAX_PAGES) {
    pageCount++;

    // M3 (round-2 review fix, refined in L4 round-3): reset Retry-After
    // accumulator at the top of each pagination iteration so a 429 on page 1
    // cannot bleed its retry delay into subsequent pages. Placing the reset
    // here (before any other page-specific logic) is robust against future
    // refactors that might add early exits between the quota check and the
    // retry loop.
    lastRetryAfterMs = undefined;

    // H6 (round-2 review fix, semantics corrected in H8 round-3): re-check
    // quota budget on page 2+. The function-top gate already covered page 1,
    // so no-op on the first iteration. On mid-pagination exhaustion we throw
    // KlaviyoQuotaExhaustedError instead of returning partial data — this
    // matches the page-1 exhaustion path and prevents silent data loss where
    // a tenant sees a truncated report with no indication it's incomplete.
    // The caller in klaviyo.ts catches the error, logs
    // `klaviyo.reporting_quota.mid_fetch_exhausted`, and breaks out of the
    // remaining report fetchers — so the whole Reporting API section is
    // short-circuited cleanly on quota exhaustion, regardless of which page
    // tripped it.
    if (pageCount > 1) {
      const paginationBudget = await checkReportingQuotaBudget(opts.kv, opts.integrationId);
      if (paginationBudget === "exhausted") {
        const count = await getReportingQuotaUsage(opts.kv, opts.integrationId);
        // eslint-disable-next-line no-console
        console.warn(JSON.stringify({
          level: "warn",
          event: "klaviyo.reporting_api.pagination_quota_stop",
          integrationId: opts.integrationId,
          endpoint: opts.endpoint,
          pagesFetchedBeforeStop: pageCount - 1,
          count,
        }));
        throw new KlaviyoQuotaExhaustedError(count, KLAVIYO_REPORTING_QUOTA_HARD_LIMIT);
      }
    }

    // Retry loop (ADR-017: 3 retries, exponential backoff, Retry-After header respect)
    let settled = false;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        const backoff = RETRY_DELAYS_MS[attempt - 1] ?? 4000;
        const delay = Math.max(lastRetryAfterMs ?? 0, backoff);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      // Only POST the first page; subsequent pages are GET to nextUrl
      const result = pageCount === 1
        ? await safeFetch(nextUrl, { method: "POST", headers, body: JSON.stringify(body) }, API_TIMEOUT_MS)
        : await safeFetch(nextUrl, { method: "GET", headers }, API_TIMEOUT_MS);

      if (!result.success) {
        if (result.error.retryable && attempt < MAX_RETRIES) {
          lastError = new Error(result.error.message);
          lastRetryAfterMs = result.error.retryAfterMs;
          continue;
        }
        throw new Error(
          sanitizeErrorMessage(
            `Klaviyo ${opts.endpoint}-values-reports failed: ${result.error.message}`
          )
        );
      }

      // Successful call: increment quota counter immediately (every successful call counts)
      await incrementReportingQuota(opts.kv, opts.integrationId);

      const parsed = (await result.data.json()) as KlaviyoValuesReportResponse<Record<string, unknown>>;
      const results = parsed.data?.attributes?.results ?? [];
      for (const entry of results) {
        entries.push({
          groupings: entry.groupings,
          statistics: entry.statistics,
        } as TEntry);
      }

      nextUrl = parsed.links?.next || null;
      settled = true;
      break;
    }

    if (!settled) {
      throw lastError ?? new Error(`Klaviyo ${opts.endpoint} report failed after retries`);
    }
  }

  return entries;
}

/** Story 12.8: Fetches per-campaign performance stats from `/api/campaign-values-reports/`. */
export async function fetchCampaignValuesReport(
  kv: KVStore,
  integrationId: string,
  accessToken: string,
  conversionMetricId: string | null,
  dateRange: { start: Date; end: Date },
  statistics: readonly string[] = KLAVIYO_CAMPAIGN_STATISTICS
): Promise<KlaviyoCampaignReportEntry[]> {
  return callReportingApi<KlaviyoCampaignReportEntry>({
    kv,
    integrationId,
    accessToken,
    endpoint: "campaign",
    dateRange,
    statistics,
    conversionMetricId,
  });
}

/** Story 12.8: Fetches per-flow-message performance stats from `/api/flow-values-reports/`. */
export async function fetchFlowValuesReport(
  kv: KVStore,
  integrationId: string,
  accessToken: string,
  conversionMetricId: string | null,
  dateRange: { start: Date; end: Date },
  statistics: readonly string[] = KLAVIYO_FLOW_STATISTICS
): Promise<KlaviyoFlowReportEntry[]> {
  return callReportingApi<KlaviyoFlowReportEntry>({
    kv,
    integrationId,
    accessToken,
    endpoint: "flow",
    dateRange,
    statistics,
    conversionMetricId,
  });
}

/** Story 12.8: Fetches per-form analytics from `/api/form-values-reports/` (no conversion_metric_id). */
export async function fetchFormValuesReport(
  kv: KVStore,
  integrationId: string,
  accessToken: string,
  dateRange: { start: Date; end: Date },
  statistics: readonly string[] = KLAVIYO_FORM_STATISTICS
): Promise<KlaviyoFormReportEntry[]> {
  return callReportingApi<KlaviyoFormReportEntry>({
    kv,
    integrationId,
    accessToken,
    endpoint: "form",
    dateRange,
    statistics,
  });
}

/** Story 12.8: Fetches per-segment growth stats from `/api/segment-values-reports/`. */
export async function fetchSegmentValuesReport(
  kv: KVStore,
  integrationId: string,
  accessToken: string,
  conversionMetricId: string | null,
  dateRange: { start: Date; end: Date },
  statistics: readonly string[] = KLAVIYO_SEGMENT_STATISTICS
): Promise<KlaviyoSegmentReportEntry[]> {
  return callReportingApi<KlaviyoSegmentReportEntry>({
    kv,
    integrationId,
    accessToken,
    endpoint: "segment",
    dateRange,
    statistics,
    conversionMetricId,
  });
}

// ============================================================================
// Story IMC-25: Tags API
// ============================================================================

/** Klaviyo tag entry from GET /api/tags */
export interface KlaviyoTag {
  id: string;
  name: string;
}

/** Response shape from GET /api/tags */
interface KlaviyoTagsListResponse {
  data: Array<{
    type: "tag";
    id: string;
    attributes: { name: string };
  }>;
  links?: { next?: string };
}

/** Response shape from GET /api/campaigns/{id}/tags */
interface KlaviyoTagsForCampaignResponse {
  data: Array<{
    type: "tag";
    id: string;
    attributes: { name: string };
  }>;
}

/**
 * Fetches all tags for the Klaviyo account.
 * Cached in KV for 24h since tags change infrequently.
 * Rate limit: 3/s burst, 60/m steady.
 */
export async function fetchAllTags(
  kv: KVStore,
  accessToken: string,
  tenantId: string,
  integrationId: string
): Promise<Map<string, KlaviyoTag>> {
  const cacheKey = `klaviyo:tags:${tenantId}:${integrationId}`;

  const cached = await kv.get(cacheKey);
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as Array<{ id: string; name: string }>;
      const map = new Map<string, KlaviyoTag>();
      for (const t of parsed) map.set(t.id, t);
      return map;
    } catch { /* cache corrupted, fetch fresh */ }
  }

  const headers = buildKlaviyoHeaders(accessToken);
  const tagMap = new Map<string, KlaviyoTag>();
  let nextUrl: string | null = `${KLAVIYO_API_BASE}/tags/?page[size]=100`;
  let pageCount = 0;

  while (nextUrl && pageCount < MAX_PAGES) {
    pageCount++;
    const result = await safeFetch(nextUrl, { method: "GET", headers }, API_TIMEOUT_MS);
    if (!result.success) break;

    try {
      const data = (await result.data.json()) as KlaviyoTagsListResponse;
      for (const entry of data.data) {
        tagMap.set(entry.id, { id: entry.id, name: entry.attributes.name });
      }
      nextUrl = data.links?.next || null;
    } catch { break; }
  }

  // Cache for 24h
  const cacheValue = JSON.stringify(Array.from(tagMap.values()));
  await kv.put(cacheKey, cacheValue, { expirationTtl: Math.floor(METRIC_CACHE_TTL_MS / 1000) });

  return tagMap;
}

/**
 * Fetches tags assigned to a specific campaign.
 * Rate limit: 3/s burst, 60/m steady. Scopes: campaigns:read, tags:read.
 */
export async function fetchTagsForCampaign(
  accessToken: string,
  campaignId: string
): Promise<KlaviyoTag[]> {
  const headers = buildKlaviyoHeaders(accessToken);
  const result = await safeFetch(
    `${KLAVIYO_API_BASE}/campaigns/${encodeURIComponent(campaignId)}/tags`,
    { method: "GET", headers },
    API_TIMEOUT_MS
  );

  if (!result.success) return [];

  try {
    const data = (await result.data.json()) as KlaviyoTagsForCampaignResponse;
    return data.data.map((entry) => ({ id: entry.id, name: entry.attributes.name }));
  } catch {
    return [];
  }
}

// ============================================================================
// Story IMC-25: Campaign Messages API (A/B test variations)
// ============================================================================

/** Campaign message entry with optional A/B test variation data */
export interface KlaviyoCampaignMessage {
  id: string;
  label: string;
  channel: string;
}

/** Campaign metadata with A/B test info */
export interface KlaviyoCampaignMeta {
  id: string;
  name: string;
  sendStrategy?: string;
  messages: KlaviyoCampaignMessage[];
}

/** Response from GET /api/campaigns with included messages */
interface KlaviyoCampaignsListResponse {
  data: Array<{
    type: "campaign";
    id: string;
    attributes: {
      name: string;
      status: string;
      archived: boolean;
      send_strategy?: { method?: string };
    };
    relationships?: {
      "campaign-messages"?: {
        data?: Array<{ type: string; id: string }>;
      };
    };
  }>;
  included?: Array<{
    type: "campaign-message";
    id: string;
    attributes: {
      label?: string;
      channel?: string;
    };
  }>;
  links?: { next?: string };
}

/**
 * Fetches campaigns with their messages to detect A/B tests.
 * A campaign with >1 message is an A/B test; each message is a variation.
 * Rate limit: 10/s burst, 150/m steady. Scopes: campaigns:read.
 *
 * Cached in KV for 6h to avoid excessive API calls.
 */
export async function fetchCampaignsWithMessages(
  kv: KVStore,
  accessToken: string,
  tenantId: string,
  integrationId: string
): Promise<Map<string, KlaviyoCampaignMeta>> {
  const cacheKey = `klaviyo:campaigns-meta:${tenantId}:${integrationId}`;
  const CAMPAIGN_META_CACHE_TTL_S = 6 * 60 * 60; // 6h

  const cached = await kv.get(cacheKey);
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as Array<KlaviyoCampaignMeta>;
      const map = new Map<string, KlaviyoCampaignMeta>();
      for (const c of parsed) map.set(c.id, c);
      return map;
    } catch { /* cache corrupted */ }
  }

  const headers = buildKlaviyoHeaders(accessToken);
  const campaignMap = new Map<string, KlaviyoCampaignMeta>();

  // Fetch campaigns with included messages for A/B test detection.
  // Klaviyo requires a channel filter on GET /api/campaigns — fetch email + SMS separately.
  const channels = ["email", "sms"] as const;
  for (const channel of channels) {
    const filterStr = `equals(messages.channel,"${channel}")`;
    let channelUrl: string | null =
      `${KLAVIYO_API_BASE}/campaigns/?filter=${encodeURIComponent(filterStr)}` +
      `&include=campaign-messages&page[size]=50`;
    let channelPageCount = 0;

    while (channelUrl && channelPageCount < MAX_PAGES) {
      channelPageCount++;
      const result = await safeFetch(channelUrl, { method: "GET", headers }, API_TIMEOUT_MS);
      if (!result.success) break;

      try {
        const data = (await result.data.json()) as KlaviyoCampaignsListResponse;

        const messageById = new Map<string, KlaviyoCampaignMessage>();
        if (data.included) {
          for (const inc of data.included) {
            if (inc.type === "campaign-message") {
              messageById.set(inc.id, {
                id: inc.id,
                label: inc.attributes.label ?? "Default",
                channel: inc.attributes.channel ?? channel,
              });
            }
          }
        }

        for (const campaign of data.data) {
          // Skip if already seen from another channel fetch
          if (campaignMap.has(campaign.id)) continue;
          const messageIds = campaign.relationships?.["campaign-messages"]?.data ?? [];
          const messages: KlaviyoCampaignMessage[] = [];
          for (const ref of messageIds) {
            const msg = messageById.get(ref.id);
            if (msg) messages.push(msg);
          }

          campaignMap.set(campaign.id, {
            id: campaign.id,
            name: campaign.attributes.name,
            sendStrategy: campaign.attributes.send_strategy?.method,
            messages,
          });
        }

        channelUrl = data.links?.next || null;
      } catch { break; }
    }
  }

  // Cache for 6h
  const cacheValue = JSON.stringify(Array.from(campaignMap.values()));
  await kv.put(cacheKey, cacheValue, { expirationTtl: CAMPAIGN_META_CACHE_TTL_S });

  return campaignMap;
}

// ============================================================================
// Story IMC-25: Segment Profile Count
// ============================================================================

/**
 * Fetches profile count for a single segment via GET /api/segments/{id}
 * with ?additional-fields[segment]=profile_count.
 *
 * CAUTION: Rate limit is very restrictive — 1/s burst, 15/m steady.
 * Call sparingly and only for segments already fetched via segment reports.
 */
export async function fetchSegmentProfileCount(
  accessToken: string,
  segmentId: string
): Promise<number | null> {
  const headers = buildKlaviyoHeaders(accessToken);
  const url = `${KLAVIYO_API_BASE}/segments/${encodeURIComponent(segmentId)}?additional-fields[segment]=profile_count`;

  const result = await safeFetch(url, { method: "GET", headers }, API_TIMEOUT_MS);
  if (!result.success) return null;

  try {
    const data = (await result.data.json()) as {
      data?: {
        attributes?: {
          profile_count?: number;
        };
      };
    };
    return data.data?.attributes?.profile_count ?? null;
  } catch {
    return null;
  }
}
